'use client';

import { forwardRef, useEffect, useRef, useState, type ReactElement, type Ref } from 'react';
import { cn, getConfig } from '@fshr/shared-react';
import { vidOpt } from '@fshr/shared';
import type { AmbientVideoPrimitive } from '@fshr/shared/types';

import Icon from '../icon';
import { useTranslations } from '@fshr/context/i18n-context';

const CODECS: Record<string, string> = {
	av1: 'video/webm; codecs=av01.0.15M.08',
	vp9: 'video/webm; codecs=vp9',
};

const BREAKPOINT_MD = 700;

const MOBILE_WIDTH = 1280;

export type Props = {
	desktop?: AmbientVideoPrimitive;
	mobile?: AmbientVideoPrimitive;
	poster?: string;
	className?: string;
	toggleClassName?: string;
	toggleIconClassName?: string;
	autoplay?: boolean;
	/**
	 * The play/pause control is required wherever the video plays on its own, so it defaults on.
	 * A caller that drives playback from its own affordance — the dining card plays on hover and
	 * stops when the pointer leaves — has nothing for the control to toggle, and it would sit on
	 * top of the card as an unexplained second button.
	 */
	showToggle?: boolean;
	/** `metadata` suits a hero that plays immediately; a hover-triggered video should fetch nothing until asked. */
	preload?: 'none' | 'metadata' | 'auto';
};

function CodecSources({
	video,
	base,
	media,
	width,
}: {
	video?: AmbientVideoPrimitive;
	base: string;
	media: string;
	width?: number;
}): ReactElement | null {
	const src = video?.src;
	if (!src) return null;
	return (
		<>
			{Object.entries(CODECS).map(([codec, type]) => (
				<source
					key={codec}
					src={`${base}${vidOpt({ path: src, codec, width })}`}
					type={type}
					media={media}
				/>
			))}
			<source src={src} type="video/mp4" media={media} />
		</>
	);
}

function AmbientVideo(
	{
		desktop,
		mobile,
		poster,
		className,
		toggleClassName,
		toggleIconClassName,
		autoplay = true,
		showToggle = true,
		preload = 'metadata',
	}: Props,
	forwardedRef: Ref<HTMLVideoElement>
): ReactElement | null {
	const t = useTranslations();
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const [isPlaying, setIsPlaying] = useState(autoplay);
	const [failed, setFailed] = useState(false);

	const hasVideo = Boolean(desktop?.src || mobile?.src);

	const desktopSource = desktop?.src ? desktop : mobile;
	const mobileSource = mobile?.src ? mobile : desktop;

	useEffect(() => {
		if (!hasVideo || failed || !autoplay) return;

		const video = videoRef.current;
		if (!video) return;
		if (!video.paused) return;

		const attempt = video.play();
		if (!attempt || typeof attempt.then !== 'function') return;

		let cancelled = false;
		attempt.then(
			() => {
				if (!cancelled) setIsPlaying(true);
			},
			(err: DOMException) => {
				if (cancelled) return;
				if (err?.name === 'NotAllowedError') setFailed(true);
				else setIsPlaying(!video.paused);
			}
		);

		return () => {
			cancelled = true;
		};
	}, [autoplay, hasVideo, failed]);

	if (!hasVideo || failed) return null;

	const { NEXT_PUBLIC_IMAGE_BASE_URL } = getConfig();
	const base = `${NEXT_PUBLIC_IMAGE_BASE_URL || ''}`;

	const togglePlayback = () => {
		const video = videoRef.current;
		if (!video) return;
		if (video.paused) {
			const attempt = video.play();
			setIsPlaying(true);
			if (attempt && typeof attempt.catch === 'function') {
				attempt.catch(() => setIsPlaying(false));
			}
		} else {
			video.pause();
			setIsPlaying(false);
		}
	};

	return (
		<div className={cn('absolute inset-0', className)}>
			<video
				ref={node => {
					videoRef.current = node;
					if (typeof forwardedRef === 'function') forwardedRef(node);
					else if (forwardedRef) forwardedRef.current = node;
				}}
				className="h-full w-full object-cover"
				poster={poster}
				muted
				loop
				playsInline
				autoPlay={autoplay}
				preload={preload}
				aria-hidden="true"
				onError={event => {
					if (event.target !== videoRef.current) return;
					const code = videoRef.current?.error?.code;
					if (
						code === undefined ||
						code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED ||
						code === MediaError.MEDIA_ERR_DECODE
					) {
						setFailed(true);
					}
				}}
				onPlay={() => setIsPlaying(true)}
				onPause={() => setIsPlaying(false)}
			>
				<CodecSources video={desktopSource} base={base} media={`(min-width: ${BREAKPOINT_MD}px)`} />
				<CodecSources
					video={mobileSource}
					base={base}
					media={`(max-width: ${BREAKPOINT_MD - 0.02}px)`}
					width={MOBILE_WIDTH}
				/>
			</video>

			{showToggle && (
				<button
					type="button"
					onClick={togglePlayback}
					aria-label={isPlaying ? t('Pause background video') : t('Play background video')}
					className={cn(
						'absolute bottom-300 right-300 z-10 flex size-200 shrink-0 items-center justify-center rounded-full bg-img-overlay-40 text-text-on-image-primary transition-colors hover:bg-img-overlay-60 focus-visible:bg-img-overlay-60 focus-visible:outline-2 focus-visible:outline-offset-2',
						toggleClassName
					)}
				>
					<Icon
						icon={isPlaying ? 'pause_button_large' : 'play_button_large'}
						className={cn('text-[20px]', toggleIconClassName)}
					/>
				</button>
			)}
		</div>
	);
}

export default forwardRef(AmbientVideo);
